import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { DateTime } from "luxon";
import { MouseEventHandler } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { CaseDiagnosis, Specimen, User } from "pwapi";

import {
  editScreenDiagnoses,
  openDocument,
  initiateModifyReportProcedure,
  createCaseDiagnosis,
} from "shared/actions/caseDetailActions";
import { useIsLoading } from "shared/hooks/requestStatusHooks";
import {
  useCurrentSpecimenDiagnoses,
  useLISFinalizedDiagnosis,
} from "shared/hooks/useDiagnosis";
import { CaseDetailState } from "shared/reducers/caseDetailReducer";
import { SlideDetailState } from "shared/reducers/slideDetailReducer";
import { selectFeatureFlag } from "shared/reducers/sysReducer";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import LinkButton from "components/LinkButton";
import SplitParagraphs from "components/SplitParagraphs";
import RequestScreenButton from "components/viewer/RequestScreenButton";
import { selectAutoCptCodesEnabled } from "selectors/autoCptCodesEnabled";
import { useAppSelector } from "store/hooks";

import ClinicalHistorySection from "../ClinicalHistorySection";
import { cptString, reduceCptCodes } from "../DiagnosisDialogHelpers";
import { DialogEnum } from "../Dialog";
import Sidebar from "../Sidebar";
import SidebarFooter from "../SidebarFooter";
import SignOutButton from "../SignOutButton";
import { getHighestVersionDiagnosis, isFinalized } from "../utils";

export type ReadOnlyDiagnosisDialogProps = {
  caseDetailObject: CaseDetailState;
  slideDetailObject: SlideDetailState;
  specimen?: Specimen;
  user?: User;
  canAmendAddend?: boolean;
  canCorrectReport?: boolean;
  isSpecimenScreened?: boolean;
  isCaseScreened?: boolean;
  hat?: { text: string; style?: "warning" | "edit" | "alert" };
  onHandleNextSpecimen: MouseEventHandler<HTMLButtonElement>;
  hasNextSpecimen?: boolean;
  dialog?: DialogEnum | null;
  setDialog?: Function;
};

const ReadOnlyDiagnosisDialog = ({
  caseDetailObject,
  slideDetailObject,
  specimen,
  user,
  canAmendAddend,
  canCorrectReport,
  isSpecimenScreened,
  isCaseScreened,
  hat,
  dialog,
  setDialog,
  onHandleNextSpecimen,
  hasNextSpecimen,
}: ReadOnlyDiagnosisDialogProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const loadingModifyReportProcedure = useIsLoading(
    `cases/${caseDetailObject?.id}/modify_report`
  );

  const screenPerSpecimen = useAppSelector((state) =>
    selectFeatureFlag(state, "screenPerSpecimen")
  );
  const showClinicalHistoryInDiagnosis = useAppSelector((state) =>
    selectFeatureFlag(state, "showClinicalHistoryInDiagnosis")
  );

  const selectedSpecimen = slideDetailObject.specimen?.id
    ? slideDetailObject.specimen
    : specimen;
  const specimenDiagnoses = useCurrentSpecimenDiagnoses(
    caseDetailObject,
    slideDetailObject,
    specimen
  );
  const sentDiagnosis = getHighestVersionDiagnosis(specimenDiagnoses || []);
  const finalDiagnosis = useLISFinalizedDiagnosis(
    caseDetailObject.case_diagnosis
  );

  const isRecallReportPeriod = caseDetailObject.transmitting_at
    ? DateTime.now() < DateTime.fromISO(caseDetailObject.transmitting_at)
    : false;

  const { diagnosis, microscopic, comment, icd10_code, zsd_code, cpt_codes } =
    sentDiagnosis || {};

  const onHandleModifyReport = (
    source:
      | CaseDiagnosis.source.ADDEND
      | CaseDiagnosis.source.AMEND
      | CaseDiagnosis.source.CORRECTION
  ) => {
    if (caseDetailObject?.id)
      dispatch(
        initiateModifyReportProcedure({
          id: caseDetailObject.id,
          requestBody: { type: source },
        })
      );
  };

  const onHandleEditScreen = () => {
    if (caseDetailObject?.id) {
      //If screening per specimen is active. A user will just create a new copy of the
      // highest version screen diagnosis as a base for a rough draft.
      if (screenPerSpecimen) {
        if (!specimen) return;
        const screenDiagnosis = getHighestVersionDiagnosis(
          caseDetailObject.case_diagnosis?.filter(
            (diag) => diag.specimen === specimen?.id
          ) || [],
          CaseDiagnosis.source.SCREEN
        );
        const newDiagDetails = {
          case: caseDetailObject.id || 0,
          specimen: specimen?.id,
          user: user?.id || 0,
          diagnosis: screenDiagnosis?.diagnosis,
          comment: screenDiagnosis?.comment,
          microscopic: screenDiagnosis?.microscopic,
          cpt_codes: screenDiagnosis?.cpt_codes,
          icd10_code: screenDiagnosis?.icd10_code,
          zsd_code: screenDiagnosis?.zsd_code,
          macro_code: screenDiagnosis?.macro_code,
          is_screen_valid: true,
          source: CaseDiagnosis.source.FINALIZE,
        };

        //@ts-ignore
        dispatch(createCaseDiagnosis(newDiagDetails));
      } else {
        dispatch(editScreenDiagnoses(caseDetailObject.id));
      }
    }
  };

  const useAutoCptCodes = useAppSelector(selectAutoCptCodesEnabled);

  return (
    <Sidebar
      hasAutoWidth
      isScrollable
      hat={
        !!hat && (
          <div
            className={classNames("FinalizedDiagnosis__header", {
              "FinalizedDiagnosis__header--warning": hat.style === "warning",
              "FinalizedDiagnosis__header--alert": hat.style === "alert",
              "FinalizedDiagnosis__header--edit": hat.style === "edit",
            })}
          >
            {(hat.style === "edit" || hat.style === "warning") && (
              <FontAwesomeIcon
                icon={hat.style === "edit" ? "pencil" : "exclamation"}
              />
            )}
            <span>{hat.text}</span>
          </div>
        )
      }
    >
      <div className="Sidebar__contentContainer DiagnosisDialog DiagnosisDialog--readonly">
        {!caseDetailObject.is_screening ? (
          <>
            {showClinicalHistoryInDiagnosis && (
              <dl className="DiagnosisDialog__clinicalHistory">
                <ClinicalHistorySection
                  clinicalHistory={selectedSpecimen?.clinical_history}
                />
              </dl>
            )}
            <dl className="DiagnosisDialog__diagnosis">
              <dt>Diagnosis</dt>
              <dd>
                {diagnosis?.split("\n").map((d, i) => (
                  <p key={i}>{d}</p>
                ))}
              </dd>
            </dl>
            <dl className="DiagnosisDialog__findings">
              <dt>Microscopic Findings</dt>
              <dd>
                <SplitParagraphs paragraph={microscopic || ""} />
              </dd>
            </dl>
            <dl className="DiagnosisDialog__comments">
              <dt>Note</dt>
              <dd>{comment}</dd>
            </dl>
            <dl className="DiagnosisDialog__codes">
              <div>
                <dt>ICD 10</dt>
                <dd>{icd10_code}</dd>
              </div>

              <div>
                <dt>ZSD</dt>
                <dd>{zsd_code}</dd>
              </div>

              {!useAutoCptCodes && (
                <div>
                  <dt>CPT Codes</dt>
                  {cpt_codes && (
                    <dd>
                      {reduceCptCodes(cpt_codes)
                        .map(
                          (cpt) =>
                            `${cptString(cpt.code)}${
                              cpt.quantity && cpt.quantity > 1
                                ? `(${cpt.quantity || 1})`
                                : ""
                            }`
                        )
                        .join(", ")}
                    </dd>
                  )}
                </div>
              )}

              {finalDiagnosis?.finalized_report_id && (
                <div>
                  <dt>
                    Diagnosis {selectedSpecimen?.name} is shown for convenience:
                  </dt>
                  <dd>
                    <LinkButton
                      title="Download Report"
                      className="Button Button--blue"
                      onClick={() => {
                        caseDetailObject.id &&
                          finalDiagnosis?.finalized_report_id &&
                          dispatch(
                            openDocument(
                              caseDetailObject.id,
                              finalDiagnosis.finalized_report_id
                            )
                          );
                      }}
                    >
                      Final Report &nbsp;{" "}
                      <FontAwesomeIcon icon="file-download" />
                    </LinkButton>
                  </dd>
                </div>
              )}
            </dl>
          </>
        ) : (
          <span>Specimen has not been requested for a screen</span>
        )}
      </div>
      {isFinalized(caseDetailObject) ? (
        (canAmendAddend || canCorrectReport) && !isRecallReportPeriod ? (
          <SidebarFooter>
            {canCorrectReport && (
              <PathologyWatchButton
                onClick={() =>
                  onHandleModifyReport(CaseDiagnosis.source.CORRECTION)
                }
                disabled={loadingModifyReportProcedure}
                color="green"
              >
                Correct Report
              </PathologyWatchButton>
            )}
            {canAmendAddend && (
              <>
                <PathologyWatchButton
                  onClick={() => {
                    onHandleModifyReport(CaseDiagnosis.source.AMEND);
                  }}
                  disabled={loadingModifyReportProcedure}
                  color="green"
                >
                  Amend
                </PathologyWatchButton>
                <PathologyWatchButton
                  onClick={() => {
                    onHandleModifyReport(CaseDiagnosis.source.ADDEND);
                  }}
                  disabled={loadingModifyReportProcedure}
                  color="green"
                >
                  Addend
                </PathologyWatchButton>
              </>
            )}
          </SidebarFooter>
        ) : undefined
      ) : // If a specimen has made it to this point while screening, the user is not expected to screen the specimen.
      caseDetailObject.is_screening ? (
        <SidebarFooter>
          {hasNextSpecimen ? (
            <PathologyWatchButton onClick={onHandleNextSpecimen} color="blue">
              Next Specimen&nbsp;&rarr;
            </PathologyWatchButton>
          ) : (
            <SignOutButton
              caseDetailObject={caseDetailObject}
              currentSlideId={slideDetailObject?.id || 0}
              user={user}
              formErrors={false}
              showSpinner
            />
          )}
        </SidebarFooter>
      ) : isSpecimenScreened ? (
        <SidebarFooter>
          <PathologyWatchButton
            onClick={() => {
              onHandleEditScreen();
            }}
            color="grey"
          >
            Edit Screen
          </PathologyWatchButton>
          <PathologyWatchButton
            onClick={() => navigate(`/cases/${caseDetailObject.id}/preview`)}
            color="green"
          >
            Preview for Sign Out &rarr;
          </PathologyWatchButton>
        </SidebarFooter>
      ) : isCaseScreened && specimenDiagnoses && !specimenDiagnoses.length ? (
        <SidebarFooter>
          <RequestScreenButton
            caseDetailObject={caseDetailObject}
            slideId={slideDetailObject?.id}
            currentUser={user}
            dialog={dialog}
            setDialog={setDialog}
          />
        </SidebarFooter>
      ) : undefined}
    </Sidebar>
  );
};

export default ReadOnlyDiagnosisDialog;
